import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import { type GQLError } from '@egw/clients'
import {
  type CustomerLegalDocumentsInput,
  type CustomerStatementsInput,
  type CustomerTaxDocumentsInput,
  type CustomerStatementsDatesOutput,
  type CustomerStatementsOutput,
  type CustomerTaxDocumentsYearsOutput,
  type CustomerTaxDocumentsOutput,
  type CustomerLegalDocumentPartnersOutput,
  type CustomerLegalDocumentsOutput,
  type InvestmentTradeConfirmAccountsOutput,
} from '../../models'
import {
  getTaxDocYearsDocument,
  getCustomerStatementsDates,
  getCustomerLegalDocumentPartners,
  getCustomerLegalDocuments,
  getCustomerStatements,
  getCustomerTaxDocuments,
  getInvestmentTradeConfirmAccounts,
} from '../../services'

/**
 * customerTaxDocumentsDates state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerStatementsDates
 */
export const useCustomerStatementsDates = (
  options?: Omit<UseQueryOptions<CustomerStatementsDatesOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerStatementsDatesOutput, GQLError> =>
  useQuery<CustomerStatementsDatesOutput, GQLError>({
    queryKey: ['customerStatementsDates'],
    queryFn: getCustomerStatementsDates,
    ...options,
  })

/**
 * customer Statements state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerStatements
 */
export const useCustomerStatements = (
  customerStatementsInput: CustomerStatementsInput,
  options?: Omit<UseQueryOptions<CustomerStatementsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerStatementsOutput, GQLError> =>
  useQuery<CustomerStatementsOutput, GQLError>({
    queryKey: ['customerStatements', customerStatementsInput.month],
    queryFn: () => getCustomerStatements(customerStatementsInput),
    ...options,
  })

/**
 * customerTaxDocumentsDates state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerTaxDocumentsDates
 */
export const useCustomerTaxDocumentsDates = (
  options?: Omit<UseQueryOptions<CustomerTaxDocumentsYearsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerTaxDocumentsYearsOutput, GQLError> =>
  useQuery<CustomerTaxDocumentsYearsOutput, GQLError>({
    queryKey: ['taxDocYearsDocument'],
    queryFn: getTaxDocYearsDocument,
    ...options,
  })

/**
 * customerLegalDocuments state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerLegalDocuments
 */
export const useCustomerTaxDocuments = (
  customerTaxDocumentsInput: CustomerTaxDocumentsInput,
  options?: Omit<UseQueryOptions<CustomerTaxDocumentsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerTaxDocumentsOutput, GQLError> =>
  useQuery<CustomerTaxDocumentsOutput, GQLError>({
    queryKey: ['customerLegalDocuments', customerTaxDocumentsInput.year],
    queryFn: () => getCustomerTaxDocuments(customerTaxDocumentsInput),
    ...options,
  })

/**
 * customerLegalDocumentPartners state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerStatementsDates
 */
export const useCustomerLegalDocumentPartners = (
  options?: Omit<UseQueryOptions<CustomerLegalDocumentPartnersOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerLegalDocumentPartnersOutput, GQLError> =>
  useQuery<CustomerLegalDocumentPartnersOutput, GQLError>({
    queryKey: ['customerLegalDocumentPartners'],
    queryFn: getCustomerLegalDocumentPartners,
    ...options,
  })

/**
 * customerLegalDocuments state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type CustomerLegalDocuments
 */
export const useCustomerLegalDocuments = (
  customerLegalDocumentsInput: CustomerLegalDocumentsInput,
  options?: Omit<UseQueryOptions<CustomerLegalDocumentsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<CustomerLegalDocumentsOutput, GQLError> =>
  useQuery<CustomerLegalDocumentsOutput, GQLError>({
    queryKey: ['customerLegalDocuments', customerLegalDocumentsInput.partnerName],
    queryFn: () => getCustomerLegalDocuments(customerLegalDocumentsInput),
    ...options,
  })

/**
 * investmentTradeConfirmAccounts state hook (includes CLOSED accounts unlike wealthOverview)
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type InvestmentTradeConfirmAccountsOutput
 */
export const useInvestmentTradeConfirmAccounts = (
  options?: Omit<UseQueryOptions<InvestmentTradeConfirmAccountsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentTradeConfirmAccountsOutput, GQLError> =>
  useQuery<InvestmentTradeConfirmAccountsOutput, GQLError>({
    queryKey: ['investmentTradeConfirmAccounts'],
    queryFn: getInvestmentTradeConfirmAccounts,
    ...options,
  })
